/**
 * VireoHR - Settings Screen
 * Styled with Gosta design system
 */

import { useNavigate } from 'react-router-dom';

import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBack = () => {
    if (user?.role === 'owner') {
      navigate('/owner/home');
    } else if (user?.role === 'employee') {
      navigate('/employee/home');
    } else if (user?.role === 'manager') {
      navigate('/manager/home');
    } else if (user?.role === 'admin') {
      navigate('/admin/tenants');
    }
  };

  const getRoleBadgeVariant = () => {
    switch (user?.role) {
      case 'admin':
        return 'error';
      case 'owner':
        return 'info';
      case 'employee':
        return 'success';
      default:
        return 'warning';
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'admin':
        return '👑 Super Admin';
      case 'owner':
        return '🏢 Business Owner';
      case 'manager':
        return '📊 Branch Manager';
      case 'employee':
        return '👤 Employee';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header title="Settings" showSettings={false} />

      {/* Main Content */}
      <main className="p-4">
        {/* Profile Section */}
        <Card className="mb-4">
          <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Profile Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
              <span className="text-sm text-[#D4AF37]">Name</span>
              <span className="text-base font-semibold text-[#0D9488]">{user?.name}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-[#D4AF37]">
              <span className="text-sm text-[#D4AF37]">Email</span>
              <span className="text-base font-semibold text-[#0D9488]">{user?.email}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-[#D4AF37]">Role</span>
              <Badge variant={getRoleBadgeVariant()}>{getRoleLabel()}</Badge>
            </div>
          </div>
        </Card>

        {/* Tenant Information (if applicable) */}
        {user?.tenantName && (
          <Card className="mb-4">
            <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Organization</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-[#D4AF37]">Company</span>
                <span className="text-base font-semibold text-[#0D9488]">{user.tenantName}</span>
              </div>
            </div>
          </Card>
        )}

        {/* Business Settings - Owner Only */}
        {user?.role === 'owner' && (
          <Card className="mb-4">
            <h3 className="text-lg font-semibold text-[#0D9488] mb-4">Business Configuration</h3>
            <Button
              variant="secondary"
              className="w-full"
              onClick={() => navigate('/owner/business-settings')}
            >
              ⚙️ Penalty & Overtime Settings
            </Button>
          </Card>
        )}

        {/* App Information */}
        <Card className="mb-4">
          <h3 className="text-lg font-semibold text-[#0D9488] mb-4">App Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-[#D4AF37]">Version</span>
              <span className="text-base font-semibold text-[#0D9488]">1.0.0</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-[#D4AF37]">Build</span>
              <span className="text-base font-semibold text-[#0D9488]">2024.01.001</span>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button variant="secondary" className="w-full" onClick={handleBack}>
            ← Back to Dashboard
          </Button>
          <Button variant="error" className="w-full" onClick={handleLogout}>
            🚪 Sign Out
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-[#D4AF37] mt-8">
          © 2024 VireoHR. All rights reserved.
        </p>
      </main>
    </div>
  );
}
